import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { FinanceClosureClientSample, FinanceClosureSummary, FinanceVendorSampleCost, FinanceNonSampleCost, FinanceClosureNote } from '../models/finance';
import { IRCalculation } from '../models/ir-calculation';

@Injectable({
  providedIn: 'root'
})
export class IncidenceRateService {
    baseUrl = environment.apiEndpoint + '/incidencerate/';

    constructor(private http: HttpClient) { }

    getIRCalculation(projectId,platform:string): Observable<IRCalculation> {
        return this.http.get<IRCalculation>(this.baseUrl + `${projectId}?platform=${platform}`);
    }

    /**
     * The calculation a project's True IR reads (NK-502). A Decipher project whose Decipher read
     * failed falls back to entering the figures by hand, which saves a separate `manual` row, so the
     * newer of its two rows wins. `decipherRow` passes a read the caller already made. Never errors.
     */
    getSavedIRCalculation(
        projectId: string,
        isDecipher: boolean,
        decipherRow?: Observable<IRCalculation | null>
    ): Observable<IRCalculation | null> {
        const read = (platform: string): Observable<IRCalculation | null> =>
            this.getIRCalculation(projectId, platform).pipe(catchError(() => of(null)));
        if (!isDecipher) {
            return read('manual');
        }
        return forkJoin({
            decipher: (decipherRow ?? read('decipher')).pipe(catchError(() => of(null))),
            manual: read('manual')
        }).pipe(map(({ decipher, manual }) => newerIRCalculation(decipher, manual)));
    }

    saveIRCalculation(calc: IRCalculation): Observable<IRCalculation> {
        return this.http.post<IRCalculation>(this.baseUrl ,calc)
    }

    saveIRCalculationDecipher(calc: IRCalculation): Observable<IRCalculation> {
        return this.http.post<IRCalculation>(this.baseUrl+'decipher' ,calc)
    }

    validateDecipherAPI(projectId): Observable<boolean> {
        return this.http.get<boolean>(this.baseUrl + `${projectId}/validate`);
    }
}

/** The more recently saved of two calculations; either may be missing. */
function newerIRCalculation(a: IRCalculation | null, b: IRCalculation | null): IRCalculation | null {
    if (!a || !b) {
        return a ?? b;
    }
    const time = (calc: IRCalculation): number => (calc.lastUpdated ? new Date(calc.lastUpdated).getTime() : 0);
    return time(b) > time(a) ? b : a;
}